import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useDroppable, DndContext, useDraggable, type DragEndEvent } from '@dnd-kit/core';
import { ChevronLeft, ChevronRight, Users } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/app/layout/AppShell';
import { useDragSensors } from '@/lib/dndSensors';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { UserAvatar } from '@/components/ui/avatar';
import { WorkloadMeter } from '@/components/shared/WorkloadMeter';
import { EmptyState } from '@/components/shared/EmptyState';
import { formatMinutes } from '@/lib/utils';
import { ApiError } from '@/lib/api';
import { useProject } from '@/features/projects';
import { useSetAssignees, useTasks, type TaskView } from '@/features/tasks/api';
import { DesktopOnly } from './DesktopOnly';
import { useLeaveOverlay, useWorkload, type WorkloadRow } from './api';

const DAY_MS = 86_400_000;

function startOfWeek(date: Date): Date {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const offset = (d.getUTCDay() + 6) % 7; // Monday-first
  d.setUTCDate(d.getUTCDate() - offset);
  return d;
}

/** Green ≤80%, amber ≤100%, red above (§5-F11). */
function PersonRow({
  row,
  onLeave,
  tasks,
}: {
  row: WorkloadRow;
  onLeave: number;
  tasks: TaskView[];
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `person:${row.userId}` });

  return (
    <tr
      ref={setNodeRef}
      className="border-b border-[var(--border)] last:border-b-0"
      style={{ background: isOver ? 'var(--accent-soft)' : undefined }}
    >
      <td className="py-2 pl-3 pr-2">
        <div className="flex items-center gap-2">
          <UserAvatar id={row.userId} name={row.name} size={24} />
          <div className="min-w-0">
            <div className="truncate text-[13px] font-medium">{row.name}</div>
            <div className="text-[11px] vt-muted">
              {row.workingDays} working days
              {onLeave > 0 ? ` · ${onLeave} on leave` : ''}
            </div>
          </div>
        </div>
      </td>

      <td className="px-2 py-2">
        <WorkloadMeter
          utilisation={row.utilisation}
          assignedMinutes={row.assignedMinutes}
          capacityMinutes={row.capacityMinutes}
          className="max-w-[220px]"
        />
      </td>

      <td className="vt-mono px-2 py-2 text-right text-[12px] vt-muted" data-testid="capacity-total">
        {formatMinutes(row.assignedMinutes)} / {formatMinutes(row.capacityMinutes)}
      </td>
      <td className="vt-mono px-2 py-2 text-right text-[12px] vt-muted">{row.taskCount}</td>
      <td className="px-2 py-2 text-right text-[11px] vt-muted">
        {tasks.length > 0 ? `${tasks.length} draggable` : ''}
      </td>
    </tr>
  );
}

function DraggableTask({ task }: { task: TaskView }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: task._id });
  return (
    <button
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className="vt-surface cursor-grab px-2 py-1 text-left text-[12px] active:cursor-grabbing"
      style={{ opacity: isDragging ? 0.4 : 1 }}
    >
      <span className="vt-mono text-[10px] vt-muted">{task.key}</span> {task.title}
    </button>
  );
}

export function WorkloadPage({ scope = 'workspace' }: { scope?: 'workspace' | 'project' }) {
  const { key } = useParams();
  const { data: project } = useProject(scope === 'project' ? key : undefined);
  const [weekOffset, setWeekOffset] = useState(0);

  const from = useMemo(
    () => new Date(startOfWeek(new Date()).getTime() + weekOffset * 7 * DAY_MS),
    [weekOffset],
  );
  const to = useMemo(() => new Date(from.getTime() + 6 * DAY_MS), [from]);

  const { data: rows, isLoading } = useWorkload(from, to, {
    projectId: scope === 'project' ? project?._id : undefined,
  });
  const { data: leave = [] } = useLeaveOverlay(from, to);
  const { data: taskData } = useTasks({
    dueFrom: from.toISOString(),
    dueTo: to.toISOString(),
    statusCategory: 'todo,in_progress',
    limit: 200,
    ...(scope === 'project' && project ? { projectId: project._id } : {}),
  });
  const setAssignees = useSetAssignees();

  const tasks = (taskData?.items ?? []) as TaskView[];
  const tasksByUser = useMemo(() => {
    const map = new Map<string, TaskView[]>();
    for (const task of tasks) {
      for (const id of task.assigneeIds) {
        map.set(id, [...(map.get(id) ?? []), task]);
      }
    }
    return map;
  }, [tasks]);

  const leaveDaysByUser = useMemo(() => {
    const map = new Map<string, number>();
    for (const entry of leave) {
      map.set(entry.userId, (map.get(entry.userId) ?? 0) + 1);
    }
    return map;
  }, [leave]);

  /** Dropping a task on a person reassigns it (§5-F11). */
  // V10 §7 — the shared sensors, so a keystroke inside an editable element
  // can never lift a draggable. See `lib/dndSensors.ts`.
  const sensors = useDragSensors();
  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || !String(over.id).startsWith('person:')) return;
    const userId = String(over.id).slice('person:'.length);
    setAssignees
      .mutateAsync({ id: String(active.id), assigneeIds: [userId] })
      .then(() => toast.success('Reassigned'))
      .catch((err: unknown) =>
        toast.error(err instanceof ApiError ? err.message : 'Could not reassign'),
      );
  }

  const label = `${from.toISOString().slice(0, 10)} → ${to.toISOString().slice(0, 10)}`;
  const overloaded = (rows ?? []).filter((r) => r.utilisation > 100);

  return (
    <DesktopOnly
      title="Workload"
      listHref={scope === 'project' && key ? `/projects/${key}/list` : '/my-tasks'}
    >
      <PageHeader
        title="Workload"
        subtitle={`${label} · capacity is leave- and holiday-adjusted`}
        actions={
          <>
            <Button variant="ghost" size="icon-sm" aria-label="Previous week" onClick={() => setWeekOffset((w) => w - 1)}>
              <ChevronLeft />
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setWeekOffset(0)}>
              This week
            </Button>
            <Button variant="ghost" size="icon-sm" aria-label="Next week" onClick={() => setWeekOffset((w) => w + 1)}>
              <ChevronRight />
            </Button>
          </>
        }
      />

      <div className="p-4">
        {overloaded.length > 0 && (
          <p
            className="mb-3 rounded-[6px] px-2.5 py-2 text-[12px]"
            style={{ background: 'color-mix(in srgb, var(--danger) 10%, transparent)', color: 'var(--danger)' }}
          >
            Over capacity this week: {overloaded.map((r) => r.name).join(', ')}
          </p>
        )}

        {isLoading ? (
          <Skeleton className="h-64" />
        ) : !rows || rows.length === 0 ? (
          <EmptyState icon={<Users className="size-6" />} title="Nobody to show" />
        ) : (
          <DndContext sensors={sensors} onDragEnd={onDragEnd}>
            <div className="vt-surface overflow-x-auto">
              <table className="w-full min-w-[720px] text-[13px]">
                <thead>
                  <tr className="border-b border-[var(--border)] text-[11px] uppercase tracking-wide vt-muted">
                    <th className="py-2 pl-3 pr-2 text-left font-medium">Person</th>
                    <th className="px-2 py-2 text-left font-medium">Utilisation</th>
                    <th className="px-2 py-2 text-right font-medium">Assigned / capacity</th>
                    <th className="px-2 py-2 text-right font-medium">Tasks</th>
                    <th className="px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <PersonRow
                      key={row.userId}
                      row={row}
                      onLeave={leaveDaysByUser.get(row.userId) ?? 0}
                      tasks={tasksByUser.get(row.userId) ?? []}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4">
              <h2 className="mb-1.5 text-[12px] font-medium uppercase tracking-wide vt-muted">
                Drag a task onto someone to reassign it
              </h2>
              <div className="flex flex-wrap gap-1.5">
                {tasks.slice(0, 40).map((task) => (
                  <DraggableTask key={task._id} task={task} />
                ))}
                {tasks.length === 0 && (
                  <p className="text-[12px] vt-muted">No tasks are due in this window.</p>
                )}
              </div>
            </div>
          </DndContext>
        )}
      </div>
    </DesktopOnly>
  );
}
